import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Marks the field invalid and wires `aria-invalid`. Pair with `aria-describedby`. */
  invalid?: boolean
  /** Decorative or interactive adornment rendered inside the field, at the start. */
  startAdornment?: ReactNode
  endAdornment?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, startAdornment, endAdornment, ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-9 w-full rounded-md border bg-card px-3 text-body-sm text-foreground',
        'placeholder:text-muted-foreground transition-colors duration-hover',
        'disabled:cursor-not-allowed disabled:opacity-60',
        invalid ? 'border-destructive' : 'border-input hover:border-muted-foreground/40',
        startAdornment ? 'pl-9' : undefined,
        endAdornment ? 'pr-9' : undefined,
        className,
      )}
      {...props}
    />
  )

  if (!startAdornment && !endAdornment) return field

  return (
    <div className="relative w-full">
      {startAdornment ? (
        <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:size-4">
          {startAdornment}
        </span>
      ) : null}
      {field}
      {endAdornment ? (
        <span className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground [&_svg]:size-4">
          {endAdornment}
        </span>
      ) : null}
    </div>
  )
})

export type FieldProps = {
  id: string
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}

/** Label + hint + error wiring. Never render a bare input in a form. */
export function Field({ id, label, hint, error, required, children }: FieldProps): ReactNode {
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-body-sm font-medium text-foreground">
        {label}
        {required ? (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div data-described-by={describedBy || undefined}>{children}</div>
      {hint ? (
        <p id={`${id}-hint`} className="text-caption text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-caption font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
